import React, {
    useMemo,
    useRef,
    useCallback,
    useState,
    useEffect,
} from "react";
import {
    DocumentCategoriesView,
    DocumentCategoriesViewSchema,
    DocumentCategoryColumn,
} from "@common/types/views/documentCategoriesView";
import { View, ViewType, ViewDensity } from "@common/types/views/views";
import { FiltersTypes } from "@common/types/filters/general";
import { ServerInferResponseBody } from "@ts-rest/core";
import { documentCategoryContract } from "@common/contracts/document-category.contract";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { ExpandedState } from "@tanstack/react-table";
import { useDocumentCategories } from "@api/documentCategory.api";
import { isUncategorizedDocumentTaxonomy } from "@common/helpers/documentCategoryFilter";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { DocumentCategoriesHeader } from "./children/DocumentCategoriesHeader";
import { DocumentCategoriesStats } from "./DocumentCategoriesStats";
import { createDocumentCategoriesViewConfig } from "./documentCategoriesViewConfig";
import { useDocumentCategoryStatistics } from "./useDocumentCategoryStatistics";
import {
    buildDocumentCategoryRows,
    getDefaultExpandedState,
    BuildDocumentCategoryRowsOptions,
    DocumentCategoryStatMap,
} from "./utils";

type DocumentCategoryStat = ServerInferResponseBody<
    typeof documentCategoryContract.getDocumentCategoryStatistics,
    200
>[number];

const defaultView: DocumentCategoriesView = {
    type: ViewType.DOCUMENT_CATEGORIES,
    columns: [
        { id: DocumentCategoryColumn.NAME },
        { id: DocumentCategoryColumn.DESCRIPTION },
        { id: DocumentCategoryColumn.PERCENTAGE },
        { id: DocumentCategoryColumn.DOCUMENTS },
        { id: DocumentCategoryColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.LG },
};

const viewConfig = createDocumentCategoriesViewConfig();

export const DocumentCategories: React.FC = () => {
    const { data: documentCategoriesData } = useDocumentCategories();
    const categories = useMemo(
        () =>
            (documentCategoriesData?.categories ?? []).filter(
                (category) =>
                    !isUncategorizedDocumentTaxonomy(category.documentTaxonomy),
            ),
        [documentCategoriesData?.categories],
    );

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<
        Pick<
            FiltersTypes,
            "end" | "start" | "entityIds" | "documentCategoryIds"
        >
    >({});

    const stats = useDocumentCategoryStatistics(filters);

    const statsMap = useMemo(
        () =>
            new Map<string, DocumentCategoryStatMap>(
                (stats.data?.body ?? []).map((stat) => [
                    stat.id,
                    { count: stat.count, groupCount: stat.groupCount },
                ]),
            ),
        [stats.data?.body],
    );

    const categoriesStatsMap = useMemo(
        () =>
            new Map<string, DocumentCategoryStat>(
                (stats.data?.body ?? []).map((stat) => [stat.id, stat]),
            ),
        [stats.data?.body],
    );

    const total = useMemo(
        () =>
            (stats.data?.body ?? []).reduce((acc, stat) => acc + stat.count, 0),
        [stats.data?.body],
    );

    const buildOptions = useMemo<BuildDocumentCategoryRowsOptions>(
        () => ({ search, filters, statsMap, total }),
        [search, filters, statsMap, total],
    );

    const rows = useMemo(
        () => buildDocumentCategoryRows(categories, buildOptions),
        [categories, buildOptions],
    );

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<DocumentCategoriesView>(
            WORKSPACE_USER_CACHE_KEYS.DOCUMENT_CATEGORIES_PAGE_VIEW_CACHE_KEY,
            DocumentCategoriesViewSchema,
        );

    const [view, setView] = useState<DocumentCategoriesView>(
        cachedView ?? defaultView,
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            setView(newView as DocumentCategoriesView);
            setCachedView(newView as DocumentCategoriesView);
        },
        [setCachedView],
    );

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    const hasInitializedExpandedRef = useRef(false);

    useEffect(() => {
        if (hasInitializedExpandedRef.current || rows.length === 0) {
            return;
        }
        hasInitializedExpandedRef.current = true;
        setExpanded(getDefaultExpandedState(rows));
    }, [rows]);

    const handleToggleCategory = useCallback(
        (val: string) => {
            const parentCategoryId =
                rows.find((row) => row.id === val)?.category
                    ?.parentCategoryId ?? val;

            if (filters.documentCategoryIds?.includes(parentCategoryId)) {
                setFilters((prev) => ({
                    ...prev,
                    documentCategoryIds: undefined,
                }));
            } else {
                setFilters((prev) => ({
                    ...prev,
                    documentCategoryIds: [parentCategoryId],
                }));
            }
        },
        [rows, filters, setFilters],
    );

    const tableMeta = useMemo(
        () => ({ entityIds: filters.entityIds }),
        [filters.entityIds],
    );

    return (
        <main>
            <DocumentCategoriesHeader
                expanded={expanded}
                tableRef={tableRef}
                search={search}
                setSearch={setSearch}
                filters={filters}
                onFiltersChange={(newFilters) =>
                    setFilters((prev) => ({ ...prev, ...newFilters }))
                }
            />

            {rows.length > 0 && total > 0 && (
                <DocumentCategoriesStats
                    statsMap={categoriesStatsMap}
                    total={total}
                    categories={rows.flatMap((r) => [r, ...(r.children ?? [])])}
                    toggleCategory={handleToggleCategory}
                />
            )}

            <ViewTable
                ref={tableRef}
                data={rows}
                view={view}
                config={viewConfig}
                onViewChange={handleViewChange}
                expanded={expanded}
                onExpandedChange={setExpanded}
                rowBorders={false}
                isParentSelectable={false}
                fixedTopOffsetPx
                recomputeTopOffsetOn={[stats.data?.body]}
                tableMeta={tableMeta}
            />
        </main>
    );
};
